import { observer } from 'mobx-react'
import React, { ReactElement, useMemo, useState } from 'react'

import Modal, { ModalFooter, ModalProps } from '../../components/Modal'
import { RADOM_COLORS } from '../../util/Constants'
import { errorToast, successToast, useTestMode } from '../../util/Util'
import { DepositMethods } from '../../util/Managed'
import { assetName, networkName } from '../../util/AssetLabels'
import { InputLabel, TextInputWithLabel } from '../../components/Input'
import { PrimaryButton } from '../../components/Button'
import Checkbox from '../../components/Checkbox'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import Radom from '../../state/Radom'

interface Leaf { network: string, token?: string, name: string, logo: string, ticker?: string }

interface Selected { network: string, token: string | undefined }

interface ICreateDepositAddressModal extends ModalProps {
  onAccountCreated: () => void
  customerId: string
}

const NATIVE = 'NATIVE'

/** The name under a token's ticker, when the ticker alone does not say it: "USD Coin" under USDC. */
const tokenName = (node: { name: string, ticker?: string }): string => {
  if (!node.ticker) return ''
  const name = assetName(node.ticker)
  return name === node.ticker ? '' : name
}

const CreateDepositAddresCustomerModal = observer(({
  title,
  onAccountCreated,
  onClose,
  visible,
  customerId
}: ICreateDepositAddressModal): ReactElement => {
  const [accountName, setAccountName] = useState('')
  const [selectedDepositMethods, setSelectedDepositMethods] =
    useState<Selected[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [testMode] = useTestMode()
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

  const createAddress = async (): Promise<void> => {
    if (selectedDepositMethods.length <= 0) {
      return
    }
    setIsLoading(true)
    try {
      await Radom.createDepositAccount({
        name: accountName,
        accountDetails: {
          Crypto: {
            depositMethods: selectedDepositMethods
              .map(m => ({
                network: m.network,
                token: m.token
              })),
            isExternal: true,
            customerId
          }
        }
      })
      onAccountCreated()
      successToast('Deposit address created')
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  const leafKey = (x: Selected | Leaf): string => `${x.network}:${x.token ?? NATIVE}`

  const collectLeaves = (node: any): Leaf[] => {
    if (node?.method) {
      return [{
        network: node.method.network,
        token: node.method.token,
        name: node.name,
        logo: node.logo,
        ticker: node.ticker
      }]
    }

    if (node?.subMethods && node.subMethods.length > 0) {
      return node.subMethods.flatMap(collectLeaves)
    }

    if (node?.name) {
      return [{
        network: node.name,
        token: undefined,
        name: node.name,
        logo: node.logo,
        ticker: node.ticker
      }]
    }

    return []
  }

  const getCheckState = (leaves: Leaf[], selectedSet: Set<string>): { all: boolean, partial: boolean } => {
    const total = leaves.length
    const sel = leaves.reduce((a, l) => a + (selectedSet.has(leafKey(l as Selected)) ? 1 : 0), 0)
    const all = total > 0 && sel === total
    const none = sel === 0
    return { all, partial: !none && !all }
  }

  const addAll = (selected: Selected[], leaves: Leaf[]): Selected[] => {
    const s = new Set(selected.map(leafKey))
    leaves.forEach(l => s.add(leafKey(l as Selected)))
    return Array.from(s).map(k => {
      const [network, tok] = k.split(':')
      return { network, token: tok === NATIVE ? undefined : tok }
    })
  }

  const removeAll = (selected: Selected[], leaves: Leaf[]): Selected[] => {
    const r = new Set(leaves.map(l => leafKey(l as Selected)))
    return selected.filter(x => !r.has(leafKey(x)))
  }

  const toggleLeaf = (selected: Selected[], leaf: Leaf): Selected[] => {
    const k = leafKey(leaf as Selected)
    const exists = selected.some(s => leafKey(s) === k)
    return exists ? selected.filter(s => leafKey(s) !== k) : [...selected, { network: leaf.network, token: leaf.token }]
  }

  const isBundledGroup = (group: { name: string }): boolean => ['EVM', 'Solana', 'Tron'].includes(group.name)

  const renderBundledGroupDetails = (group: any): ReactElement => {
    const branches = group?.subMethods ?? []
    const hasNestedBranches = branches.some(
      (branch: any) => Array.isArray(branch?.subMethods) && branch.subMethods.length > 0
    )

    const rows = hasNestedBranches
      ? branches.map((branch: any) => ({
        label: networkName(branch.name),
        assets: (branch.subMethods ?? []).map((item: any) => item.ticker ?? item.name).join(', ')
      }))
      : [{
        label: 'Assets enabled',
        assets: branches.map((item: any) => item.ticker ?? item.name).join(', ')
      }]

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
        padding: '0 12px 12px 46px'
      }}>
        {rows.map((row) => (
          <div key={`${group.name}-${row.label}`} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ color: RADOM_COLORS.GRAY_DARKEST, fontSize: 12.5, fontWeight: 500 }}>{row.label}</span>
            <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 12.25, lineHeight: 1.45 }}>{row.assets}</span>
          </div>
        ))}
      </div>
    )
  }

  const topGroups = useMemo(
    () => DepositMethods.filter(d => (testMode ? d.isTestnet : !d.isTestnet)),
    [testMode]
  )
  const selectedSet = useMemo(() => new Set(selectedDepositMethods.map(leafKey)), [selectedDepositMethods])
  const selectedLeafCount = useMemo(() => selectedDepositMethods.length, [selectedDepositMethods])

  return <Modal
    presentation='drawer'
    title={title}
    visible={visible}
    onClose={onClose}>

    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'start',
      gap: 16
    }}>
      <div style={{
        padding: '14px 16px',
        borderRadius: 16,
        border: '1px solid var(--border-soft, #eceff3)',
        background: 'var(--surface-subtle, #f9fafb)',
        color: RADOM_COLORS.GRAY_DARKEST,
        fontSize: 13.5,
        lineHeight: 1.55
      }}>
        Create a contact-linked deposit address so incoming funds are easier to trace back to the right person.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        <TextInputWithLabel
          label='Address nickname'
          placeholder='Enter a name for this deposit address'
          disabled={isLoading}
          required
          value={accountName}
          onChange={(e) => {
            const el = (e.target as HTMLInputElement)
            const value = el.value
            setAccountName(value)
          }}
        />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: '14px 16px',
          borderRadius: 16,
          border: '1px solid var(--border-soft, #eceff3)',
          background: 'var(--surface-subtle, #fcfcfd)'
        }}>
          <InputLabel>Deposit methods</InputLabel>
          <Dropdown
            innerStyle={{ maxHeight: 300 }}
            disabled={isLoading}
            selectedContent={
              selectedLeafCount > 0
                ? <span style={{ fontSize: 14 }}>{selectedLeafCount} methods selected</span>
                : <span style={{ fontSize: 14 }}>Select deposit methods</span>
            }
            dropdownContent={
              <div onMouseDown={(e) => e.stopPropagation()}>
                {topGroups.map((group, idx) => {
                  const groupLeaves = collectLeaves(group)
                  const groupState = getCheckState(groupLeaves, selectedSet)
                  const isBundled = isBundledGroup(group)
                  const isExpanded = !!expandedGroups[group.name]

                  return (
                    <div key={`g-${idx}`}>
                      <DropdownItem
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedDepositMethods(prev => groupState.all
                            ? removeAll(prev, groupLeaves)
                            : addAll(prev, groupLeaves))
                        }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                            <Checkbox checked={groupState.all || groupState.partial} />
                            <div
                              style={{
                                height: 25,
                                width: 25,
                                backgroundImage: `url(${group.logo})`,
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: 'contain',
                                backgroundPosition: 'center',
                                position: 'relative'
                              }} />
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                              <span style={{ fontSize: 14 }}>{networkName(group.name)}</span>
                              {group.description &&
                              <span style={{ fontSize: 12, opacity: 0.5 }}>{group.description}</span>}
                            </div>
                          </div>
                          {isBundled &&
                            <button
                              type='button'
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setExpandedGroups((prev) => ({
                                  ...prev,
                                  [group.name]: !prev[group.name]
                                }))
                              }}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                color: RADOM_COLORS.GRAY_DARKER,
                                cursor: 'pointer',
                                fontSize: 12.5,
                                fontWeight: 500,
                                padding: 0,
                                flexShrink: 0
                              }}>
                              {isExpanded ? 'Hide details' : 'Show details'}
                            </button>}
                        </div>
                      </DropdownItem>
                      {isBundled && isExpanded && renderBundledGroupDetails(group)}

                      {!isBundled && (group.subMethods ?? []).map((net: any, jdx: number) => {
                        const netLeaves = collectLeaves(net)
                        const netState = getCheckState(netLeaves, selectedSet)
                        const isLeafNode = !!net?.method

                        if (isLeafNode) {
                          const leaf = netLeaves[0]
                          const leafState = getCheckState([leaf], selectedSet)
                          return (
                            <div key={`l-${idx}-${jdx}`}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 45 }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20, height: '100%' }}>
                                  <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8 }} />
                                  <div style={{ width: 10, height: 1, backgroundColor: RADOM_COLORS.GRAY8 }} />
                                </div>
                                <DropdownItem
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setSelectedDepositMethods(prev => toggleLeaf(prev, leaf))
                                  }}
                                  style={{ width: '100%' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <Checkbox checked={leafState.all || leafState.partial} />
                                    <div
                                      style={{
                                        height: 25,
                                        width: 25,
                                        backgroundImage: `url(${leaf.logo})`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: 'contain',
                                        backgroundPosition: 'center',
                                        position: 'relative'
                                      }} />
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <span style={{ fontSize: 14 }}>{leaf.ticker ?? leaf.name}</span>
                                      {tokenName(leaf) &&
                                        <span style={{ fontSize: 12, opacity: 0.5 }}>{tokenName(leaf)}</span>}
                                    </div>
                                  </div>
                                </DropdownItem>
                              </div>
                            </div>
                          )
                        }

                        return (
                          <div key={`n-${idx}-${jdx}`}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 45 }}>
                              <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20, height: '100%' }}>
                                <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8 }} />
                                <div style={{ width: 10, height: 1, backgroundColor: RADOM_COLORS.GRAY8 }} />
                              </div>
                              <DropdownItem
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSelectedDepositMethods(prev =>
                                    netState.all
                                      ? removeAll(prev, netLeaves)
                                      : addAll(prev, netLeaves))
                                }}
                                style={{ width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <Checkbox checked={netState.all || netState.partial} />
                                  <div
                                    style={{
                                      height: 25,
                                      width: 25,
                                      backgroundImage: `url(${net.logo})`,
                                      backgroundRepeat: 'no-repeat',
                                      backgroundSize: 'contain',
                                      backgroundPosition: 'center',
                                      position: 'relative'
                                    }} />
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: 14 }}>{networkName(net.name)}</span>
                                    {net.ticker && <span style={{ fontSize: 12, opacity: 0.5 }}>{net.ticker}</span>}
                                  </div>
                                </div>
                              </DropdownItem>
                            </div>

                            {(net.subMethods ?? []).map((tok: any, kdx: number) => {
                              const tokenLeaves = collectLeaves(tok)
                              const leaf = tokenLeaves[0]
                              const leafState = getCheckState(tokenLeaves, selectedSet)

                              return (
                                <div key={`t-${idx}-${jdx}-${kdx}`} style={{ display: 'flex', alignItems: 'center', gap: 4, height: 48 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', marginLeft: 20, height: '100%' }}>
                                    <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8 }} />
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', marginLeft: 25, height: '100%' }}>
                                    <div style={{ width: 1, height: '100%', backgroundColor: RADOM_COLORS.GRAY8 }} />
                                    <div style={{ width: 10, height: 1, backgroundColor: RADOM_COLORS.GRAY8 }} />
                                  </div>

                                  <DropdownItem
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setSelectedDepositMethods(prev => toggleLeaf(prev, leaf))
                                    }}
                                    style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                      <Checkbox checked={leafState.all || leafState.partial} />
                                      <div
                                        style={{
                                          height: 25,
                                          width: 25,
                                          backgroundImage: `url(${tok.logo})`,
                                          backgroundRepeat: 'no-repeat',
                                          backgroundSize: 'contain',
                                          backgroundPosition: 'center',
                                          position: 'relative'
                                        }} />
                                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontSize: 14 }}>{tok.ticker ?? tok.name}</span>
                                        {tokenName(tok) &&
                                          <span style={{ fontSize: 12, opacity: 0.5 }}>{tokenName(tok)}</span>}
                                      </div>
                                    </div>
                                  </DropdownItem>
                                </div>
                              )
                            })}
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            }
          />
        </div>

      </div>
    </div>

    <ModalFooter $fullWidthAction>
      <PrimaryButton
        isLoading={isLoading}
        disabled={!accountName || selectedDepositMethods.length <= 0 }
        style={{ width: '100%' }}
        onClick={() => {
          createAddress()
        }}>
          Create deposit address
      </PrimaryButton>
    </ModalFooter>

  </Modal>
})

export default CreateDepositAddresCustomerModal
